import React from 'react';
import { WebsiteDesignProps } from './types';
import { Scroll, Bookmark, PenTool, GraduationCap } from 'lucide-react';
import { Button } from "@/components/ui/button";

const IvyLeagueTradition: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Kingsbridge University Prep";

    return (
        <div className="bg-[#0a0a0a] text-[#e0e0e0] min-h-screen font-serif">
            {/* Top Gold Bar */}
            <div className="h-1 bg-gradient-to-r from-[#721c24] via-[#b68d40] to-[#721c24]"></div>

            <nav className="bg-[#121212] border-b border-[#333] py-6 relative z-20">
                <div className="container mx-auto px-6 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-14 bg-[#721c24] flex items-center justify-center text-[#b68d40] rounded-b-lg shadow-lg border-t-0 border border-[#b68d40] pt-2">
                             <GraduationCap size={28} />
                        </div>
                        <div>
                             <h1 className="text-xl tracking-widest uppercase font-bold text-[#e0e0e0]">{schoolName}</h1>
                             <span className="text-[10px] text-[#b68d40] uppercase tracking-[0.3em] block">Veritas • Honor • Disciplina</span>
                        </div>
                    </div>
                    
                    <div className="hidden md:flex gap-10 text-xs font-bold uppercase tracking-widest text-[#888]">
                         <a href="#" className="hover:text-[#b68d40] transition-colors">Academics</a>
                         <a href="#" className="hover:text-[#b68d40] transition-colors">Residential Life</a>
                         <a href="#" className="hover:text-[#b68d40] transition-colors">Athletics</a>
                         <a href="#" className="hover:text-[#b68d40] transition-colors">Giving</a>
                    </div>
                    
                    <Button variant="outline" className="border-[#b68d40] text-[#b68d40] hover:bg-[#b68d40] hover:text-[#121212] uppercase tracking-widest text-xs font-bold rounded-none px-6">
                        Inquire
                    </Button>
                </div>
            </nav>

            {/* Hero */}
            <header className="relative py-32 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover opacity-20 sepia" alt="Library" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10 grid grid-cols-1 lg:grid-cols-2">
                    <div>
                         <div className="w-20 h-1 bg-[#b68d40] mb-8"></div>
                         <h2 className="text-6xl md:text-7xl mb-8 leading-tight font-medium">
                             Cultivating the <br/>
                             <span className="text-[#b68d40] italic">Intellect</span> & <br/>
                             Character.
                         </h2>
                         <p className="text-xl text-[#888] font-light leading-relaxed mb-10 max-w-xl italic">
                             "The function of education is to teach one to think intensely and to think critically. Intelligence plus character - that is the goal of true education."
                         </p>
                         <div className="flex gap-6">
                             <Button className="bg-[#721c24] hover:bg-[#5a161c] text-white rounded-none px-8 py-6 uppercase tracking-widest shadow-lg border border-[#b68d40]/30">
                                 Admission 2026
                             </Button>
                             <Button variant="link" className="text-[#e0e0e0] hover:text-[#b68d40] uppercase tracking-widest">
                                 Read the Prospectus
                             </Button>
                         </div>
                    </div>
                </div>
            </header>

            {/* Stat Band */}
            <div className="border-y border-[#333] bg-[#121212] py-12">
                <div className="container mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-[#333]">
                    {[
                        { label: "Founded", val: "1892" },
                        { label: "Student-Faculty Ratio", val: "6:1" },
                        { label: "AP Courses", val: "24" },
                        { label: "Ivy League Admin", val: "95%" },
                    ].map((stat, i) => (
                        <div key={i} className="px-4">
                            <div className="text-4xl font-bold text-[#b68d40] mb-2">{stat.val}</div>
                            <div className="text-xs uppercase tracking-widest text-[#666]">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            <section className="py-24 bg-[#0a0a0a]">
                <div className="container mx-auto px-6 text-center max-w-4xl">
                    <div className="mb-12 flex justify-center text-[#b68d40]"><Scroll size={40} /></div>
                    <h3 className="text-3xl italic font-light text-[#e0e0e0] mb-12 leading-relaxed">
                        We are a community of scholars dedicated to the pursuit of excellence. 
                        Our rigorous curriculum is grounded in the liberal arts tradition, yet forward-looking 
                        in its preparation for a complex global society.
                    </h3>
                    <div className="flex justify-center gap-12">
                         <div className="text-center group cursor-pointer">
                             <div className="w-48 h-64 mb-6 overflow-hidden border border-[#333] relative">
                                 <img src="https://images.unsplash.com/photo-1519452635265-7b1fbfd1e4e0?q=80&w=1974&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-70 group-hover:opacity-100" alt="Student" />
                                 <div className="absolute inset-0 border-[12px] border-[#0a0a0a]"></div>
                             </div>
                             <h4 className="uppercase tracking-widest text-sm font-bold text-[#b68d40]">The Student Body</h4>
                         </div>
                         <div className="text-center group cursor-pointer mt-12 md:mt-0">
                             <div className="w-48 h-64 mb-6 overflow-hidden border border-[#333] relative">
                                 <img src="https://images.unsplash.com/photo-1577896334614-53b708b1a8d0?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-70 group-hover:opacity-100" alt="Faculty" />
                                  <div className="absolute inset-0 border-[12px] border-[#0a0a0a]"></div>
                             </div>
                             <h4 className="uppercase tracking-widest text-sm font-bold text-[#b68d40]">The Faculty</h4>
                         </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default IvyLeagueTradition;
